import { Typography } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import tw from 'twin.macro'
import { ClientPublicWrapper } from 'src/components/layout/client/ClientPublicWrapper'
import { OpportunityApplicationForm } from 'src/components/opportunity-application-form/OpportunityApplicationForm'
import { FetchPublicOpportunity } from 'src/constants/actionTypes'
import { PublicOpportunity } from 'src/interfaces/opportunity'
import { LoadingPage } from 'src/pages/LoadingPage'
import { createAsyncAction } from 'src/utils/reduxUtils'

/**
 * Public application form for an opportunity.
 *
 * Deliberately self-contained: the opaque token is the only key, there is no :partnerName param, and it reads
 * nothing from the onboarding or driver redux slices. That is what keeps it liftable into its own app when the
 * candidate-facing surface moves to its own domain.
 */
export const OpportunityApplyPage = () => {
  const dispatch = useDispatch()
  const params = useParams()

  const publicToken = params.publicToken || ''

  const [opportunity, setOpportunity] = useState<PublicOpportunity | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isUnavailable, setIsUnavailable] = useState<boolean>(false)
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
  const [selectedMarketId, setSelectedMarketId] = useState<string>('')

  useEffect(() => {
    const load = async () => {
      if (!publicToken) {
        setIsUnavailable(true)
        setIsLoading(false)

        return
      }

      try {
        const result: PublicOpportunity = await createAsyncAction(
          dispatch,
          FetchPublicOpportunity.request({ publicToken }),
        )

        setOpportunity(result)
      } catch {
        // A missing token, a closed posting, and a partner without the product are all the same to a visitor.
        setIsUnavailable(true)
      } finally {
        setIsLoading(false)
      }
    }

    load()
  }, [dispatch, publicToken])

  const handleSuccess = useCallback(() => {
    setIsSubmitted(true)
  }, [])

  const isMultiMarket = (opportunity?.markets?.length || 0) > 1

  const eyebrow = useMemo(() => {
    if (!opportunity) {
      return ''
    }

    if (!isMultiMarket) {
      return opportunity.markets[0]?.title || ''
    }

    if (!selectedMarketId) {
      return 'Select your market'
    }

    const market = opportunity.markets.find((item) => {
      return item.id === selectedMarketId
    })

    return market?.title || ''
  }, [opportunity, isMultiMarket, selectedMarketId])

  if (isLoading) {
    return <LoadingPage flow="client" />
  }

  if (isUnavailable || !opportunity) {
    return (
      <ClientPublicWrapper>
        {/* No mt here: the wrapper already gives 24px of top padding, and adding to it leaves the short card
            top-heavy against its 16px bottom. */}
        <Typography variant="h2" css={tw`mb-2`}>
          This opportunity is not available
        </Typography>
        <Typography css={tw`text-shades-400`}>
          It may have been closed, or the link may be incomplete. If someone sent you this recently,
          ask them for the current link.
        </Typography>
      </ClientPublicWrapper>
    )
  }

  if (isSubmitted) {
    return (
      <ClientPublicWrapper>
        <Typography variant="h2" css={tw`mb-2`}>
          Application received
        </Typography>
        <Typography css={tw`text-shades-400`}>
          {`${opportunity.partnerDisplayName} has your details for ${opportunity.title}. If they select you, you will get an email with a link to start onboarding — and you will not be asked for any of this again.`}
        </Typography>
      </ClientPublicWrapper>
    )
  }

  return (
    <ClientPublicWrapper>
      <Typography
        variant="body2"
        css={tw`mt-4 uppercase tracking-wide text-primary-500 font-semibold`}
        data-testid="opportunity-apply-eyebrow"
      >
        {eyebrow}
      </Typography>
      <Typography variant="h2" css={tw`mb-2`}>
        {opportunity.title}
      </Typography>
      <Typography css={tw`mb-6 text-shades-400 whitespace-pre-line`}>
        {opportunity.description}
      </Typography>

      <OpportunityApplicationForm
        publicToken={publicToken}
        opportunity={opportunity}
        onMarketChange={setSelectedMarketId}
        onSuccess={handleSuccess}
      />
    </ClientPublicWrapper>
  )
}
